/**
 * Keyboard runtime: components bind handlers to catalogue ids; one document listener resolves a
 * keydown through the registry (scope stack, typing guard) and calls the winning handler.
 */
import { useEffect, useRef } from 'react';
import { registry } from './catalog.js';
import { ariaKeyShortcuts, formatCombo } from './registry.js';
const handlers = new Map();
const scopes = [];
let listeners = 0;
const TYPING = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';
export function isTypingTarget(t) {
    return !!t?.closest?.(TYPING);
}
function onKeyDown(e) {
    if (e.defaultPrevented || e.isComposing)
        return;
    const def = registry.resolve(e, {
        active: scopes.map((s) => s.scope),
        typing: isTypingTarget(e.target),
        bound: (id) => (handlers.get(id)?.length ?? 0) > 0,
    });
    if (!def)
        return;
    const stack = handlers.get(def.id);
    const h = stack?.[stack.length - 1];
    if (!h)
        return;
    if (h(e) === false)
        return;
    e.preventDefault();
}
function install() {
    if (listeners++ === 0)
        document.addEventListener('keydown', onKeyDown);
    return () => {
        if (--listeners === 0)
            document.removeEventListener('keydown', onKeyDown);
    };
}
/** Bind `handler` to catalogue id `id` while `enabled`. */
export function useShortcut(id, handler, enabled = true) {
    const latest = useRef(handler);
    latest.current = handler;
    useEffect(() => {
        if (!enabled)
            return;
        const fn = (e) => latest.current(e);
        const stack = handlers.get(id) ?? [];
        stack.push(fn);
        handlers.set(id, stack);
        const off = install();
        return () => {
            off();
            const cur = handlers.get(id) ?? [];
            const i = cur.indexOf(fn);
            if (i >= 0)
                cur.splice(i, 1);
            if (!cur.length)
                handlers.delete(id);
        };
    }, [id, enabled]);
}
/** Mark `scope` live (a page, component or open dialog) while `active`. */
export function useShortcutScope(scope, active = true) {
    useEffect(() => {
        if (!active)
            return;
        const entry = { token: {}, scope };
        scopes.push(entry);
        const off = install();
        return () => {
            off();
            const i = scopes.indexOf(entry);
            if (i >= 0)
                scopes.splice(i, 1);
        };
    }, [scope, active]);
}
/** Test hook: forget every binding and scope. */
export function resetShortcutRuntime() {
    handlers.clear();
    scopes.length = 0;
}
// ---- labels ----------------------------------------------------------------------------------
export function shortcutDef(id) {
    return registry.get(id);
}
/** Platform label of the primary combo ("⌥D" / "Alt+D"), or "" for an unknown id. */
export function shortcutLabel(id, mac) {
    const d = registry.get(id);
    return d ? formatCombo(d.display ?? d.keys[0] ?? '', mac) : '';
}
/** Every combo of a def, formatted (cheat sheet). */
export function shortcutLabels(def, mac) {
    return def.display ? [formatCombo(def.display, mac)] : def.keys.map((k) => formatCombo(k, mac));
}
/** Props for a control: `aria-keyshortcuts` (screen readers) and a `title` tooltip with the key. */
export function shortcutProps(id, title) {
    const d = registry.get(id);
    if (!d)
        return title ? { title } : {};
    const aria = d.keys.map(ariaKeyShortcuts).join(' ');
    return {
        'aria-keyshortcuts': aria,
        title: `${title ?? d.label} (${shortcutLabel(id)})`,
    };
}
//# sourceMappingURL=runtime.js.map